import type { FunctionComponent, MouseEventHandler } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import BoardsIconGlyph from '@atlaskit/icon/core/boards';
import BillingIconGlyph from '@atlaskit/icon/core/credit-card';
import ExportIconGlyph from '@atlaskit/icon/core/download';
import PeopleGroupIconGlyph from '@atlaskit/icon/core/people-group';
import PowerUpsIconGlyph from '@atlaskit/icon/core/project';
import SettingsIconGlyph from '@atlaskit/icon/core/settings';
import WarningIconGlyph from '@atlaskit/icon/core/warning';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { useWorkspaceId, WorkspaceIdProvider } from '@trello/id-context';
import { Button } from '@trello/nachos/button';
import { Icon } from '@trello/nachos/icon';
import { OverflowMenuHorizontalIcon } from '@trello/nachos/icons/overflow-menu-horizontal';
import { Popover, usePopover } from '@trello/nachos/popover';
import { PopoverMenu, PopoverMenuLink } from '@trello/nachos/popover-menu';
import { token } from '@trello/theme';

import { MaybePremiumLozenge } from 'app/src/components/FreeTrial';
import { useWorkspaceSettingsNavigationLinks } from 'app/src/components/WorkspaceNavigation/useWorkspaceSettingsNavigationLinks';

const BoardsIcon = () => (
  <Icon glyph={() => <BoardsIconGlyph spacing="spacious" label="" />} />
);
const MembersIcon = () => (
  <Icon glyph={() => <PeopleGroupIconGlyph spacing="spacious" label="" />} />
);
const ExportIcon = () => (
  <Icon glyph={() => <ExportIconGlyph spacing="spacious" label="" />} />
);
const BillingIcon = () => (
  <Icon glyph={() => <BillingIconGlyph spacing="spacious" label="" />} />
);
const PowerUpsIcon = () => (
  <Icon glyph={() => <PowerUpsIconGlyph spacing="spacious" label="" />} />
);
const SettingsIcon = () => (
  <Icon glyph={() => <SettingsIconGlyph spacing="spacious" label="" />} />
);
const WarningIcon = () => (
  <Icon
    glyph={() => (
      <WarningIconGlyph
        spacing="spacious"
        label=""
        color={token('color.icon.warning', '#E56910')}
      />
    )}
  />
);

const isExternal = (href: string) =>
  new URL(href, document.baseURI).origin !== new URL(document.baseURI).origin;

interface WorkspaceMoreButtonProps {
  workspaceId: string;
}

const WorkspaceMoreMenuLinks: FunctionComponent = () => {
  const workspaceId = useWorkspaceId();
  const links = useWorkspaceSettingsNavigationLinks();

  const onClickHandler = (linkTarget: string) => () => {
    Analytics.sendClickedLinkEvent({
      linkName: 'expandWorkspaceSectionMoreOptionsLinkItem',
      source: 'boardSwitcherModal',
      attributes: {
        linkTarget,
      },
      containers: formatContainers({
        workspaceId,
      }),
    });

    boardSwitcherSharedState.setValue({
      isOpen: false,
    });
  };

  return (
    <PopoverMenu>
      <PopoverMenuLink
        href={links.boards.href}
        onClick={onClickHandler('boards')}
        iconBefore={<BoardsIcon />}
      >
        <FormattedMessage
          id="templates.board_switcher.boards"
          defaultMessage="Boards"
          description="Text on the boards button on the workspace more menu"
        />
      </PopoverMenuLink>
      {links.members.isVisible && (
        <PopoverMenuLink
          href={links.members.href}
          onClick={onClickHandler('members')}
          iconBefore={<MembersIcon />}
        >
          <FormattedMessage
            id="templates.board_switcher.members"
            defaultMessage="Members"
            description="Text on the members button on the workspace more menu"
          />
        </PopoverMenuLink>
      )}
      {links.settings.isVisible && (
        <PopoverMenuLink
          href={links.settings.href}
          onClick={onClickHandler('settings')}
          iconBefore={<SettingsIcon />}
        >
          <FormattedMessage
            id="templates.board_switcher.settings"
            defaultMessage="Settings"
            description="Text on the settings button on the workspace more menu"
          />
        </PopoverMenuLink>
      )}
      {links.powerUps.isVisible && (
        <PopoverMenuLink
          href={links.powerUps.href}
          onClick={onClickHandler('power-ups')}
          iconBefore={<PowerUpsIcon />}
          iconAfter={
            <MaybePremiumLozenge featureHighlighted="workspace-navigation-settings" />
          }
        >
          <FormattedMessage
            id="templates.board_switcher.power-ups"
            defaultMessage="Power-Ups"
            description="Text on the power-ups button on the workspace more menu"
          />
        </PopoverMenuLink>
      )}
      {links.upgrade.isVisible && (
        <PopoverMenuLink
          href={links.upgrade.href}
          target={isExternal(links.upgrade.href) ? '_blank' : undefined}
          onClick={onClickHandler('billing')}
          iconBefore={<BillingIcon />}
        >
          <FormattedMessage
            id="templates.board_switcher.upgrade-workspace"
            defaultMessage="Upgrade workspace"
            description="Text on the billing button on the workspace more menu for a free workspace"
          />
        </PopoverMenuLink>
      )}
      {links.billingIssue.isVisible && (
        <PopoverMenuLink
          href={links.billingIssue.href}
          target={isExternal(links.billingIssue.href) ? '_blank' : undefined}
          onClick={onClickHandler('billing')}
          iconBefore={<WarningIcon />}
        >
          <FormattedMessage
            id="templates.board_switcher.billing-issue"
            defaultMessage="Billing issue!"
            description="Text on the billing button on the workspace more menu for a workspace that has a problem with its payment method"
          />
        </PopoverMenuLink>
      )}
      {links.billing.isVisible && (
        <PopoverMenuLink
          href={links.billing.href}
          target={isExternal(links.billing.href) ? '_blank' : undefined}
          onClick={onClickHandler('billing')}
          iconBefore={<BillingIcon />}
        >
          <FormattedMessage
            id="templates.board_switcher.billing"
            defaultMessage="Billing"
            description="Text on the billing button on the workspace more menu for a paid workspace"
          />
        </PopoverMenuLink>
      )}
      {links.export.isVisible && (
        <PopoverMenuLink
          href={links.export.href}
          onClick={onClickHandler('export')}
          iconBefore={<ExportIcon />}
          iconAfter={
            <MaybePremiumLozenge featureHighlighted="workspace-navigation-settings" />
          }
        >
          <FormattedMessage
            id="templates.board_switcher.export"
            defaultMessage="Export"
            description="Text on the export button on the workspace more menu"
          />
        </PopoverMenuLink>
      )}
    </PopoverMenu>
  );
};

export const WorkspaceMoreButton: FunctionComponent<
  WorkspaceMoreButtonProps
> = ({ workspaceId }) => {
  const intl = useIntl();

  const { toggle, triggerRef, popoverProps } = usePopover();

  const toggleMenu: MouseEventHandler<HTMLButtonElement> = (event) => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'expandWorkspaceSectionMoreButton',
      containers: formatContainers({
        workspaceId,
      }),
      source: 'boardSwitcherModal',
    });

    event.stopPropagation();
    toggle();
  };

  return (
    <>
      <Button
        ref={triggerRef}
        onClick={toggleMenu}
        iconBefore={<OverflowMenuHorizontalIcon />}
        appearance="subtle"
        aria-label={intl.formatMessage({
          id: 'templates.board_switcher.more-options',
          defaultMessage: 'More options',
          description: 'Label on workspace more button',
        })}
        aria-expanded={popoverProps.isVisible}
      />
      <Popover {...popoverProps}>
        <WorkspaceIdProvider value={workspaceId}>
          <WorkspaceMoreMenuLinks />
        </WorkspaceIdProvider>
      </Popover>
    </>
  );
};
